/**
 * TrackingConnectPanel – First-party tracking integration panel.
 * Lets users configure tracking domain, get embed snippet, see stats.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Crosshair,
  Globe,
  Code,
  BarChart3,
  ExternalLink,
  Tag,
} from "lucide-react";
import TrackingTagRulesPanel from "./tracking-tag-rules-panel";
import TrackingUrlRulesPanel from "./tracking-url-rules-panel";
import TrackingConversionRulesPanel from "./tracking-conversion-rules-panel";

interface TrackingConfig {
  exists: boolean;
  config: {
    enabled?: boolean;
    trackingDomain?: string | null;
    useCustomDomain?: boolean;
    cookieDomain?: string | null;
    requireConsent?: boolean;
    trackPageViews?: boolean;
  };
}

interface TrackingSnippet {
  domain: string;
  isCustomDomain: boolean;
  requireConsent: boolean;
  snippet: string;
  hardcodedSnippet: string;
  consentModeSnippet: string;
  directSnippet: string;
  scriptUrl: string;
  userId: string;
}

interface TrackingStats {
  visitors: number;
  events: number;
  conversions: number;
  sessions: number;
}

interface TrackingConnectPanelProps {
  onInsightsLoaded?: (context: string) => void;
  forUserId?: string;
  defaultExpanded?: boolean;
}

function CopyBtn({ text }: { text: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="p-1.5 rounded-md bg-white/[0.06] hover:bg-white/10 text-white/60 hover:text-white transition-colors"
      title={t("trackingPanel.copy")}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-emerald-400" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  );
}

const PLATFORMS = [
  { id: "html", label: "HTML / Static", icon: "🌐" },
  { id: "wordpress", label: "WordPress", icon: "📝" },
  { id: "clickfunnels", label: "ClickFunnels", icon: "🔧" },
  { id: "ghl", label: "LeadConnector", icon: "🟢" },
  { id: "shopify", label: "Shopify", icon: "🛍️" },
  { id: "webflow", label: "Webflow", icon: "🎨" },
  { id: "wix", label: "Wix", icon: "🖼️" },
  { id: "kajabi", label: "Kajabi", icon: "📚" },
  { id: "typeform", label: "TypeForm", icon: "📋" },
] as const;

function getPlatformGuide(
  platformId: string,
  snippet: string,
  t: (k: string, opts?: Record<string, unknown>) => string
): string {
  return t(`trackingPanel.guide.${platformId}`, {
    snippet,
    defaultValue: t("trackingPanel.guide.default", { snippet }),
  });
}

export default function TrackingConnectPanel({
  onInsightsLoaded,
  forUserId,
  defaultExpanded,
}: TrackingConnectPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showDetails, setShowDetails] = useState(defaultExpanded ?? false);
  const [showSnippet, setShowSnippet] = useState(defaultExpanded ?? false);
  const [customDomain, setCustomDomain] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState("html");
  const [consentMode, setConsentMode] = useState(false);
  const fuParam = forUserId ? `?forUserId=${forUserId}` : "";

  const { data: configData, isLoading: configLoading } = useQuery<TrackingConfig>({
    queryKey: ["tracking-config", forUserId],
    queryFn: () => apiRequest("GET", `/api/tracking/config${fuParam}`),
    staleTime: 30_000,
  });

  const { data: snippetData } = useQuery<TrackingSnippet>({
    queryKey: ["tracking-snippet", forUserId],
    queryFn: () => apiRequest("GET", `/api/tracking/snippet${fuParam}`),
    staleTime: 30_000,
  });

  const { data: stats } = useQuery<TrackingStats>({
    queryKey: ["tracking-stats", forUserId],
    queryFn: () => apiRequest("GET", `/api/tracking/stats${fuParam}`),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (configData?.config) {
      setCustomDomain(configData.config.trackingDomain || "");
      setUseCustom(configData.config.useCustomDomain || false);
      setConsentMode(configData.config.requireConsent || false);
    }
  }, [configData]);

  useEffect(() => {
    if (stats && onInsightsLoaded) {
      onInsightsLoaded(
        `Tracking stats: ${stats.visitors} visitors, ${stats.events} events, ${stats.conversions} conversions, ${stats.sessions} sessions.`
      );
    }
  }, [stats, onInsightsLoaded]);

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/tracking/config", { ...data, ...(forUserId ? { forUserId } : {}) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracking-config"] });
      queryClient.invalidateQueries({ queryKey: ["tracking-snippet"] });
    },
  });

  if (configLoading) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-red-400" />
        <span className="text-sm text-white/60">{t("trackingPanel.checking")}</span>
      </div>
    );
  }

  const isEnabled = configData?.config?.enabled !== false;
  const hasStats = stats && (stats.visitors > 0 || stats.events > 0);

  return (
    <div
      className={`p-3 border rounded-lg space-y-3 ${
        isEnabled && hasStats
          ? "bg-white/[0.04] border-red-500/30"
          : "bg-white/[0.04] border-white/[0.08]"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-red-600/20 flex items-center justify-center">
            <Crosshair className="w-3.5 h-3.5 text-red-400" />
          </div>
          <span className="text-sm font-medium text-white">Tracking Script</span>
          {isEnabled && hasStats ? (
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("trackingPanel.active")}
            </Badge>
          ) : isEnabled ? (
            <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
              {t("trackingPanel.waitingData")}
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs border-white/[0.1] text-white/60">
              {t("trackingPanel.disabled")}
            </Badge>
          )}
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="p-1 rounded hover:bg-white/[0.06] text-white/60"
        >
          {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Quick stats if tracking is active */}
      {hasStats && !showDetails && (
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="bg-white/[0.04] rounded-lg p-2">
            <div className="text-xs text-white/50">{t("trackingPanel.stats.visitors")}</div>
            <div className="text-sm font-semibold text-white">{stats!.visitors}</div>
          </div>
          <div className="bg-white/[0.04] rounded-lg p-2">
            <div className="text-xs text-white/50">{t("trackingPanel.stats.events")}</div>
            <div className="text-sm font-semibold text-white">{stats!.events}</div>
          </div>
          <div className="bg-white/[0.04] rounded-lg p-2">
            <div className="text-xs text-white/50">{t("trackingPanel.stats.conversions")}</div>
            <div className="text-sm font-semibold text-emerald-400">{stats!.conversions}</div>
          </div>
          <div className="bg-white/[0.04] rounded-lg p-2">
            <div className="text-xs text-white/50">{t("trackingPanel.stats.sessions")}</div>
            <div className="text-sm font-semibold text-white">{stats!.sessions}</div>
          </div>
        </div>
      )}

      {showDetails && (
        <div className="space-y-4 pt-1 border-t border-white/[0.06]">
          {/* Domain Selection */}
          <div>
            <h4 className="text-xs font-medium text-white/70 mb-2 flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" /> {t("trackingPanel.domain.title")}
            </h4>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tracking-domain"
                  checked={!useCustom}
                  onChange={() => setUseCustom(false)}
                  className="accent-red-500"
                />
                <span className="text-sm text-white/80">
                  {t("trackingPanel.domain.default")}:{" "}
                  <code className="text-emerald-400 bg-[#030407] px-1.5 py-0.5 rounded text-xs">
                    t.viral.partners
                  </code>
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tracking-domain"
                  checked={useCustom}
                  onChange={() => setUseCustom(true)}
                  className="accent-red-500"
                />
                <span className="text-sm text-white/80">{t("trackingPanel.domain.custom")}</span>
              </label>
              {useCustom && (
                <div className="ml-6 space-y-2">
                  <input
                    type="text"
                    value={customDomain}
                    onChange={(e) => setCustomDomain(e.target.value)}
                    placeholder="t.twojastrona.com"
                    className="w-full h-8 px-3 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-red-500/50"
                  />
                  <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <p className="text-xs text-amber-300">
                      <strong>{t("trackingPanel.domain.dnsTitle")}:</strong>{" "}
                      {t("trackingPanel.domain.dnsDesc")}
                    </p>
                    <code className="block mt-1 text-xs text-emerald-400 bg-[#030407]/50 px-2 py-1 rounded">
                      {customDomain || "t.twojastrona.com"} → CNAME → t.viral.partners
                    </code>
                    <p className="text-xs text-amber-300/70 mt-1.5">
                      {t("trackingPanel.domain.firstParty")}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <Button
              size="sm"
              className="mt-3 bg-red-600 hover:bg-red-700 text-white text-xs"
              disabled={saveMutation.isPending}
              onClick={() =>
                saveMutation.mutate({
                  useCustomDomain: useCustom,
                  trackingDomain: useCustom ? customDomain : null,
                  requireConsent: consentMode,
                  enabled: true,
                })
              }
            >
              {saveMutation.isPending ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("trackingPanel.saving")}
                </>
              ) : (
                t("trackingPanel.saveConfig")
              )}
            </Button>
            {saveMutation.isSuccess && (
              <span className="ml-2 text-xs text-emerald-400">✓ {t("trackingPanel.saved")}</span>
            )}
          </div>

          {/* Consent Mode Toggle */}
          <div>
            <h4 className="text-xs font-medium text-white/70 mb-2 flex items-center gap-1.5">
              <Code className="w-3.5 h-3.5" /> {t("trackingPanel.consent.title")}
            </h4>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="consent-mode"
                  checked={!consentMode}
                  onChange={() => setConsentMode(false)}
                  className="accent-red-500 mt-0.5"
                />
                <div>
                  <span className="text-sm text-white/80">
                    {t("trackingPanel.consent.hardcoded")}
                  </span>
                  <p className="text-xs text-white/40 mt-0.5">
                    {t("trackingPanel.consent.hardcodedDesc")}
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="consent-mode"
                  checked={consentMode}
                  onChange={() => setConsentMode(true)}
                  className="accent-red-500 mt-0.5"
                />
                <div>
                  <span className="text-sm text-white/80">
                    {t("trackingPanel.consent.consentMode")}{" "}
                    <span className="text-xs bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5 ml-1">
                      {t("trackingPanel.consent.gdprBadge")}
                    </span>
                  </span>
                  <p className="text-xs text-white/40 mt-0.5">
                    {t("trackingPanel.consent.consentModeDesc")}
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Snippet */}
          <div>
            <button
              onClick={() => setShowSnippet(!showSnippet)}
              className="flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white transition-colors"
            >
              <Code className="w-3.5 h-3.5" />
              {showSnippet ? t("trackingPanel.snippet.hide") : t("trackingPanel.snippet.show")}
              {showSnippet ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </button>

            {showSnippet && snippetData && (
              <div className="mt-2 space-y-3">
                {consentMode && (
                  <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                    <p className="text-xs text-emerald-300">
                      <strong>{t("trackingPanel.snippet.consentActiveTitle")}</strong>{" "}
                      {t("trackingPanel.snippet.consentActiveDesc")}
                    </p>
                  </div>
                )}

                {/* Platform selector */}
                <div>
                  <p className="text-xs text-white/50 mb-1.5">
                    {t("trackingPanel.snippet.choosePlatform")}:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {PLATFORMS.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setSelectedPlatform(p.id)}
                        className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                          selectedPlatform === p.id
                            ? "bg-red-600/20 border-red-500/40 text-red-300"
                            : "bg-white/[0.04] border-white/[0.08] text-white/60 hover:text-white hover:border-white/20"
                        }`}
                      >
                        {p.icon} {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Snippet code */}
                {(() => {
                  const activeSnippet = consentMode
                    ? (snippetData.consentModeSnippet ??
                      `<script>\nwindow._gfyRequireConsent=true;\nvar head=document.head;\nvar script=document.createElement('script');\nscript.type='text/javascript';\nscript.src="${snippetData.scriptUrl}";\nhead.appendChild(script);\n</script>`)
                    : (snippetData.hardcodedSnippet ?? snippetData.snippet);
                  return (
                    <div className="relative">
                      <div className="absolute top-2 right-2 z-10">
                        <CopyBtn text={activeSnippet} />
                      </div>
                      <pre className="bg-[#030407] border border-white/[0.1] rounded-lg p-3 pr-10 overflow-x-auto text-xs">
                        <code className="text-emerald-400 font-mono whitespace-pre">
                          {activeSnippet}
                        </code>
                      </pre>
                    </div>
                  );
                })()}

                {/* Platform-specific instructions */}
                <div className="p-3 bg-white/[0.03] border border-white/[0.08] rounded-lg">
                  <h5 className="text-xs font-medium text-white/80 mb-1.5">
                    {PLATFORMS.find((p) => p.id === selectedPlatform)?.icon}{" "}
                    {t("trackingPanel.snippet.instruction")}:{" "}
                    {PLATFORMS.find((p) => p.id === selectedPlatform)?.label}
                  </h5>
                  <pre className="text-xs text-white/60 whitespace-pre-wrap font-sans leading-relaxed">
                    {getPlatformGuide(
                      selectedPlatform,
                      consentMode
                        ? (snippetData.consentModeSnippet ??
                            `<script>\nwindow._gfyRequireConsent=true;\nvar head=document.head;\nvar script=document.createElement('script');\nscript.type='text/javascript';\nscript.src="${snippetData.scriptUrl}";\nhead.appendChild(script);\n</script>`)
                        : (snippetData.hardcodedSnippet ?? snippetData.snippet),
                      t
                    )}
                  </pre>
                </div>

                {/* Domain info */}
                <div className="flex items-center gap-2 text-xs text-white/40">
                  <Globe className="w-3 h-3" />
                  <span>
                    {t("trackingPanel.snippet.domain")}:{" "}
                    <code className="text-emerald-400">{snippetData.domain}</code>
                    {snippetData.isCustomDomain && ` ${t("trackingPanel.snippet.customSuffix")}`}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Stats (expanded) */}
          {hasStats && (
            <div>
              <h4 className="text-xs font-medium text-white/70 mb-2 flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5" /> {t("trackingPanel.stats.title")}
              </h4>
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="bg-white/[0.04] rounded-lg p-2">
                  <div className="text-xs text-white/50">{t("trackingPanel.stats.visitors")}</div>
                  <div className="text-sm font-semibold text-white">{stats!.visitors}</div>
                </div>
                <div className="bg-white/[0.04] rounded-lg p-2">
                  <div className="text-xs text-white/50">{t("trackingPanel.stats.events")}</div>
                  <div className="text-sm font-semibold text-white">{stats!.events}</div>
                </div>
                <div className="bg-white/[0.04] rounded-lg p-2">
                  <div className="text-xs text-white/50">
                    {t("trackingPanel.stats.conversions")}
                  </div>
                  <div className="text-sm font-semibold text-emerald-400">{stats!.conversions}</div>
                </div>
                <div className="bg-white/[0.04] rounded-lg p-2">
                  <div className="text-xs text-white/50">{t("trackingPanel.stats.sessions")}</div>
                  <div className="text-sm font-semibold text-white">{stats!.sessions}</div>
                </div>
              </div>
            </div>
          )}

          {/* Tag Rules */}
          <div className="border-t border-white/[0.06] pt-3">
            <TrackingTagRulesPanel />
          </div>

          {/* URL Rules */}
          <div className="border-t border-white/[0.06] pt-3">
            <TrackingUrlRulesPanel />
          </div>

          {/* Conversion Rules */}
          <div className="border-t border-white/[0.06] pt-3">
            <TrackingConversionRulesPanel />
          </div>

          {/* Documentation link */}
          <a
            href="/docs/tracking"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            {t("trackingPanel.docs")}
          </a>
        </div>
      )}
    </div>
  );
}
